import { regex } from 'arkregex';
import { compact } from 'es-toolkit';
import { env } from '@/env';
import { escapeRegExp, QUOTE_TRIM_PATTERN, WHITESPACE_SPLIT_PATTERN } from '@/lib/utils/regex';

const EMBEDDABLE_STATUS_VALUES = ['allow', 'deny', 'unknown'] as const;
export type EmbeddableStatus = (typeof EMBEDDABLE_STATUS_VALUES)[number];

const DEFAULT_APP_ORIGINS = buildDefaultAppOrigins();
const VALID_STATUSES = new Set<string>(EMBEDDABLE_STATUS_VALUES);
const isEmbeddableStatus = (value: unknown): value is EmbeddableStatus =>
  typeof value === 'string' && VALID_STATUSES.has(value);

export function normalizeEmbeddableStatus(value: string | null | undefined): EmbeddableStatus {
  return isEmbeddableStatus(value) ? value : 'unknown';
}

export function detectEmbeddableStatus(
  headers: Headers,
  options?: { appOrigins?: readonly string[] }
): EmbeddableStatus {
  const appOrigins = new Set((options?.appOrigins ?? DEFAULT_APP_ORIGINS).map((origin) => origin.toLowerCase()));

  const csp = headers.get('content-security-policy');
  if (csp) {
    const decision = evaluateFrameAncestors(csp, appOrigins);
    if (decision) {
      return decision;
    }
  }

  const xFrameOptions = headers.get('x-frame-options');
  if (xFrameOptions) {
    return evaluateXFrameOptions(xFrameOptions, appOrigins);
  }

  return 'allow';
}

function buildDefaultAppOrigins(): string[] {
  const origins = new Set<string>();
  const candidates = [env.NEXT_PUBLIC_APP_URL, env.PRODUCTION_APP_URL, env.AUTH_URL, 'http://localhost:3000'];

  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== 'string') {
      continue;
    }
    try {
      origins.add(new URL(candidate).origin.toLowerCase());
    } catch {
      // ignore invalid URLs
    }
  }

  return [...origins];
}

function evaluateXFrameOptions(value: string, appOrigins: Set<string>): EmbeddableStatus {
  const normalized = value.split(',')[0]?.trim().toLowerCase();
  if (!normalized) {
    return 'unknown';
  }

  if (normalized === 'deny') {
    return 'deny';
  }

  if (normalized === 'sameorigin') {
    return 'deny';
  }

  if (normalized.startsWith('allow-from')) {
    const parts = normalized.split(WHITESPACE_SPLIT_PATTERN);
    const origin = parts[1];
    if (origin && matchesAllowedOrigin(origin, appOrigins)) {
      return 'allow';
    }
    return 'deny';
  }

  if (normalized === 'allowall') {
    return 'allow';
  }

  return 'unknown';
}

function evaluateFrameAncestors(policy: string, appOrigins: Set<string>): EmbeddableStatus | null {
  const directive = extractFrameAncestorsDirective(policy);
  if (!directive) {
    return null;
  }

  if (directive.length === 0) {
    return 'deny';
  }

  for (const raw of directive) {
    const token = stripQuotes(raw);
    if (!token) {
      continue;
    }

    const lower = token.toLowerCase();
    if (lower === 'none') {
      return 'deny';
    }
    if (lower === 'self') {
      continue;
    }
    if (lower === '*') {
      return 'allow';
    }
    if (lower === 'https:' || lower === 'http:') {
      return 'allow';
    }

    if (matchesAllowedOrigin(token, appOrigins)) {
      return 'allow';
    }
  }

  return 'deny';
}

function extractFrameAncestorsDirective(policy: string): string[] | null {
  const segments = compact(policy.split(';').map((segment) => segment.trim()));

  for (const segment of segments) {
    if (!segment.toLowerCase().startsWith('frame-ancestors')) {
      continue;
    }

    const remainder = segment.slice('frame-ancestors'.length).trim();
    if (!remainder) {
      return [];
    }

    return compact(remainder.split(WHITESPACE_SPLIT_PATTERN));
  }

  return null;
}

function matchesAllowedOrigin(token: string, appOrigins: Set<string>): boolean {
  const normalizedToken = stripQuotes(token);
  if (!normalizedToken) {
    return false;
  }

  const resolved = normalizeOrigin(normalizedToken);
  if (resolved && appOrigins.has(resolved)) {
    return true;
  }

  if (normalizedToken.includes('*')) {
    return [...appOrigins].some((origin) => wildcardSourceMatches(normalizedToken, origin));
  }

  return false;
}

function normalizeOrigin(input: string): string | null {
  try {
    return new URL(input).origin.toLowerCase();
  } catch {
    return null;
  }
}

function wildcardSourceMatches(patternInput: string, origin: string): boolean {
  const sanitized = stripQuotes(patternInput);
  if (!sanitized) {
    return false;
  }

  const [schemeRaw, rest] = sanitized.split('://');
  if (!(schemeRaw && rest)) {
    return false;
  }

  const scheme = schemeRaw.toLowerCase();

  const lowerOrigin = origin.toLowerCase();
  if (!lowerOrigin.startsWith(`${scheme}://`)) {
    return false;
  }

  const originHostPort = lowerOrigin.slice(scheme.length + 3);
  const [patternHostRaw, patternPort] = rest.split(':');
  if (!patternHostRaw) {
    return false;
  }
  const patternHost = patternHostRaw;
  const [originHostRaw, originPort] = originHostPort.split(':');
  const originHost = originHostRaw ?? '';

  if (patternPort && patternPort !== originPort) {
    return false;
  }

  if (!patternHost.includes('*')) {
    return patternHost === originHost;
  }

  if (patternHost === '*') {
    return true;
  }

  if (patternHost.startsWith('*.')) {
    const suffix = patternHost.slice(2);
    return originHost === suffix || originHost.endsWith(`.${suffix}`);
  }

  const escaped = escapeRegExp(patternHost).replaceAll(String.raw`\*`, '.*');
  const originPattern = regex(`^${escaped}$`);
  return originPattern.test(originHost);
}

function stripQuotes(value: string): string {
  return value.replaceAll(QUOTE_TRIM_PATTERN, '');
}
